import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getAdminUser } from "../api/adminClient";
import { useNexusOptional } from "../ai/NexusProvider";
import { useTheme } from "../context/ThemeContext";
import {
  getAdminWorkspaceDisplayName,
  getWorkspaceBrandStyle,
  getAdminPdfBranding,
} from "../utils/workspacePdfBranding";
import CommandCenterPanel from "./CommandCenterPanel";
import { buildPlatformCommandContext } from "./assistantPageContext";
import { ROUTES } from "./routes";
import "./nexusChat.css";

export default function NexusChatPage() {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const nexus = useNexusOptional();
  const panelRef = useRef(null);
  const handoffConsumedRef = useRef(null);
  const user = getAdminUser();

  const workspaceName = useMemo(
    () => getAdminWorkspaceDisplayName(),
    [user?.tenant_id, user?.product_display_name, user?.tenant_name, nexus?.workspaceBrandingRevision]
  );
  const pageBrandStyle = useMemo(
    () => getWorkspaceBrandStyle(getAdminPdfBranding()),
    [user?.tenant_id, user?.product_display_name, nexus?.workspaceBrandingRevision]
  );

  const platformContext = useMemo(
    () => buildPlatformCommandContext({ currentRoute: ROUTES.nexusChat }),
    []
  );

  useEffect(() => {
    const handoff = nexus?.chatHandoff;
    if (!handoff || handoffConsumedRef.current === handoff.id) return;
    handoffConsumedRef.current = handoff.id;
    nexus?.clearChatHandoff?.();

    const run = async () => {
      nexus?.startNewChat?.();
      nexus?.bumpConversations?.();
      await new Promise((resolve) => window.setTimeout(resolve, 0));
      if (handoff.mode === "workflow" && handoff.guideId) {
        panelRef.current?.startWorkflowChat?.(handoff.guideId);
        if (handoff.message) {
          await panelRef.current?.runWorkflow?.(handoff.message, { guideId: handoff.guideId });
        }
        return;
      }
      if (handoff.message) {
        await panelRef.current?.runInsight?.(handoff.message);
      }
    };
    run();
  }, [nexus?.chatHandoff, nexus]);

  return (
    <div
      className={`nexus-chat-page${dark ? " nexus-chat-page--dark" : " nexus-chat-page--light"}`}
      data-theme={dark ? "dark" : "light"}
      style={pageBrandStyle}
    >
      <header className="nexus-chat-page-header">
        <Link to={ROUTES.dashboard} className="nexus-chat-back">
          <ArrowLeft size={16} aria-hidden />
          Back to dashboard
        </Link>
        <div className="nexus-chat-page-heading">
          <p className="nexus-chat-page-eyebrow">{workspaceName}</p>
          <h1 className="nexus-chat-page-title">NEXUS conversation</h1>
        </div>
      </header>

      <div className="nexus-chat-page-body">
        <CommandCenterPanel
          ref={panelRef}
          context={platformContext}
          pathname={ROUTES.nexusChat}
          showInsights
          hideWorkflows={false}
          usePlatformChat
          registerWorkflow
          variant="chat"
          placeholderOverride="Ask anything or describe what you want to achieve…"
        />
      </div>
    </div>
  );
}
